import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { hub, SignedOut, type HubSite, type Me } from "./lib/hub";
import { Activity } from "./hub/Activity";
import { Home } from "./hub/Home";
import { Login } from "./hub/Login";
import { Shell } from "./hub/Shell";
import { Users } from "./hub/Users";
import { TasmaSite } from "./sites/tasma/TasmaSite";

// Loaded only when a ReserVella-kind dashboard is opened.
const ReservellaSite = lazy(() => import("./sites/reservella/ReservellaSite").then((m) => ({ default: m.ReservellaSite })));
// Loaded only when an AFAQ-kind dashboard is opened.
const AfaqSite = lazy(() => import("./sites/afaq/AfaqSite").then((m) => ({ default: m.AfaqSite })));

// The hub's screens. Signed out: the sign-in page. Signed in: the websites this person may open (home), each website's
// dashboard (/sites/:id), and for owners the Users and Activity screens. Who may do what is decided by the hub's
// Worker and by each website; the screens only leave out what someone can't use.

type Session = { me: Me; sites: HubSite[] } | "signed-out" | { error: string } | null;

export function App() {
  const location = useLocation();
  const [session, setSession] = useState<Session>(null);
  const onLogin = location.pathname === "/login";

  useEffect(() => {
    if (onLogin) return;
    let live = true;
    Promise.all([hub.me(), hub.sites()]).then(
      ([me, sites]) => live && setSession({ me, sites }),
      (error: unknown) => live && setSession(error instanceof SignedOut ? "signed-out" : { error: error instanceof Error ? error.message : "The hub didn't answer." }),
    );
    return () => {
      live = false;
    };
  }, [onLogin]);

  if (onLogin) return <Login />;
  if (session === "signed-out") return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (session === null) return <p className="grid min-h-svh place-items-center text-midnight/70">Loading…</p>;
  if ("error" in session) return <p className="grid min-h-svh place-items-center p-6 text-center text-midnight/80">{session.error}</p>;

  const { me, sites } = session;
  return (
    <Routes>
      <Route path="/sites/:siteId/*" element={<SiteDashboard sites={sites} me={me} />} />
      <Route element={<Shell me={me} />}>
        <Route index element={<Home sites={sites} me={me} />} />
        <Route path="users" element={me.role === "owner" ? <Users sites={sites} me={me} /> : <Navigate to="/" replace />} />
        <Route path="activity" element={me.role === "owner" ? <Activity /> : <Navigate to="/" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** A website's dashboard: the screens of its kind (src/sites/<kind>/). */
function SiteDashboard({ sites, me }: { sites: HubSite[]; me: Me }) {
  const { siteId = "" } = useParams();
  const site = sites.find((s) => s.id === siteId);
  if (!site) return <Navigate to="/" replace />;
  if (site.kind === "reservella" || site.kind === "afaq") {
    const Screens = site.kind === "afaq" ? AfaqSite : ReservellaSite;
    return (
      <Suspense fallback={<p className="grid min-h-svh place-items-center text-midnight/70">Loading…</p>}>
        <Screens sites={sites} me={me} />
      </Suspense>
    );
  }
  return <TasmaSite sites={sites} />;
}
